/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * List Data Sagas
 *
 * @author Evgenij Tyan
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import { createElement } from 'react';

import get from 'lodash/get';
import keys from 'lodash/keys';
import { SagaIterator } from 'redux-saga';
import { call, fork, put, select } from 'redux-saga/effects';

import { requestSaga } from '@/main/sagas/requestSaga';
import { checkESTableList } from '@/main/utils/checkESTable';
import { API_HOSTNAME, TABLE_LIST } from '@modules/app/Api';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationPageAnalyzer,
} from '@modules/internationalization/intSelectors';
import { logBusinessEvent } from '@modules/logging/loggingActions';
import { ELogBusinessEventType } from '@modules/logging/loggingConstants';
import ModalPopup from '@modules/modal/components/ModalPopup';
import { modalOpen } from '@modules/modal/modalActions';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import { makeSelectIsDensity } from '@modules/storage/storageSelectors';

import {
    collapseTableSubRows,
    loadTableListError,
    loadTableListFully,
    loadTableListSuccessful,
    sendFieldSuggestSuccessful,
} from '../actions/tableActions';
import { mapTableWithOwnSaga, TABLE_DENSITY_LOAD_SIZE, TABLE_LOAD_SIZE } from '../constants';
import { ITableReducer } from '../reducers/tableReducer';
import {
    makeSelectorBasketContext,
    makeSelectorTableCount,
    makeSelectorTableCurrentName,
    makeSelectorTableData,
    makeSelectorTableIsLoaded,
    makeSelectorTableSearchAfterValues,
} from '../selectors/tableSelectors';
import {
    getIdFromTableName,
    MapTableNameToGetAccountIdSaga,
    MapTableNameToListApiPath,
    MapTableNameToLoggerLoadList,
} from '../types/tableMapTypes';
import { ILoadTableListPayload, ILoadTableListSuccessfulPayload, TableNameType } from '../types/tableTypes';
import { getOptions } from './loadTableMetaSagas';

/**
 *
 * @param action
 * @return []
 */
export function* loadTableListSaga(action: { payload: ILoadTableListPayload; type: string }): SagaIterator {
    const tableName: TableNameType = yield select(makeSelectorTableCurrentName());

    /**
     * If table has its own saga then fork to it
     */
    const ownListSaga = mapTableWithOwnSaga[tableName];
    if (ownListSaga) {
        yield fork(ownListSaga, action);
        return;
    }

    const force: boolean = get(action, 'payload.force', false);
    const context = yield select(makeSelectorBasketContext());
    const isDensity = yield select(makeSelectIsDensity());
    const tableLoadSize = isDensity ? TABLE_DENSITY_LOAD_SIZE : TABLE_LOAD_SIZE;
    const translation = yield select(makeSelectTranslationCommon());

    let list = yield select(makeSelectorTableData(tableName));
    const count = yield select(makeSelectorTableCount());
    const tableSearchAfterValues = yield select(makeSelectorTableSearchAfterValues());

    yield put(collapseTableSubRows(true));
    if (!force && count > 0 && list.length >= count) {
        yield put(loadTableListFully());
    } else {
        try {
            if (force) {
                list = [];
            }
            const url = MapTableNameToListApiPath[tableName] + TABLE_LIST;

            const queryParams = [
                `from=${list.length}`,
                `size=${tableLoadSize}`,
                `use_search_after=${checkESTableList(url)}`,
                context ? `context=${context}` : '',
            ].filter(Boolean);
            let id = 0;
            const getIdSaga = MapTableNameToGetAccountIdSaga[tableName];
            if (getIdSaga) {
                id = yield call(getIdSaga);
            }
            if (id) {
                queryParams.push(`id=${id}`);
            }
            if (
                checkESTableList(url) &&
                Array.isArray(tableSearchAfterValues) &&
                !!tableSearchAfterValues.length &&
                !!list.length
            ) {
                queryParams.push(`search_after_values=${tableSearchAfterValues.join(',')}`);
            }
            const queryString = queryParams.join('&');
            const tableData = yield call(requestSaga, API_HOSTNAME + url + '?' + queryString, 'GET');
            if (
                (tableName === TableNameType.expensesAnalyzer || tableName === TableNameType.payments) &&
                tableData.isCorrupted
            ) {
                const expensesAnalyzerTranslation = yield select(makeSelectTranslationPageAnalyzer());
                yield put(
                    modalOpen({
                        render: (props: IModalRenderProps) =>
                            createElement(ModalPopup, {
                                dataName: 'modal-popup-data-is-corrupted',
                                message: expensesAnalyzerTranslation.dataIsCorrupted,
                                onCloseModal: props.onCloseModal,
                            }),
                    }),
                );
            }

            // Для некоторых таблиц фасеты для фильтров могут приходить вместе со списком
            let metaFacets: ILoadTableListSuccessfulPayload['metaFacets'] = undefined;
            if (tableData.facets) {
                const newOptions = getOptions(tableData.facets, translation);
                metaFacets = keys(newOptions) as ITableReducer[TableNameType]['metaFacets'];
                yield put(sendFieldSuggestSuccessful(newOptions));
            }

            const data: any[] = tableData?.elements ?? [];
            let dataFormat = [];
            const tableFooter: any = tableData?.summary ?? undefined;
            const count = get(tableData, 'count', 0);
            const basketAllIn = get(tableData, 'basketAllIn', false);
            const searchAfterValues = tableData?.searchAfterValues;

            if (data) {
                dataFormat = data.map((item) => ({
                    ...item,
                    id: getIdFromTableName(tableName, item),
                    subRows: [{ expandedColumn: translation.commonTable.loading }],
                }));
            }

            // We should send event at first time
            const wasLoaded = yield select(makeSelectorTableIsLoaded());
            if (!wasLoaded) {
                const eventName = MapTableNameToLoggerLoadList[tableName];
                if (eventName) {
                    yield put(
                        logBusinessEvent({
                            eventName,
                            type: ELogBusinessEventType.common,
                        }),
                    );
                }
            }

            const currentTableName: TableNameType = yield select(makeSelectorTableCurrentName());
            // if current page fit to table name is loaded
            if (tableName === currentTableName) {
                yield put(
                    loadTableListSuccessful({
                        basketAllIn,
                        count,
                        data: [].concat(list, dataFormat as []),
                        metaFacets, // перезаписывает фасеты, только если они пришли
                        tableFooter,
                        tableName,
                        searchAfterValues,
                    }),
                );
            }
            yield put(collapseTableSubRows(false));

            if (!data.length) {
                yield put(loadTableListFully());
                return data;
            }


            return data;
        } catch (error: any) {
            yield put(loadTableListError({ tableName: tableName, error: error.toString() }));
            yield put(collapseTableSubRows(false));
        }
        yield put(collapseTableSubRows(false));
        return [];
    }
}
